import { useState, useEffect, useCallback, useRef } from "react";
import { Lock, Inbox, Loader2, Phone, Mail, Building2, RefreshCw, CheckCheck, Plus, Pencil, Trash2, X, LogOut, Upload, Image as ImageIcon, FileText } from "lucide-react";
import {
  fetchContacts, fetchStats, updateContactStatus, adminLogin,
  adminList, adminCreate, adminUpdate, adminDelete, uploadImage, toAbsolute,
  fetchSubscribers, deleteSubscriber, fetchSettings, updateSettings, deleteContact,
  previewEmail, adminListBookings, adminUpdateBooking, adminDeleteBooking,
  adminListTickets, adminUpdateTicket, adminDeleteTicket,
} from "@/lib/api";
import { BrandMark } from "@/components/site/BrandMark";
import { toast } from "sonner";

const STORE_KEY = "madinaty_admin_key";

const SCHEMAS = {
  blog: {
    label: "المدونة",
    empty: { title: "", category: "", cover_image: "", author: "فريق مدينتي", read_time: "٣ دقائق", excerpt: "", content: "", published: true },
    fields: [
      { k: "title", l: "العنوان", type: "text" },
      { k: "category", l: "التصنيف", type: "text" },
      { k: "cover_image", l: "صورة الغلاف", type: "image" },
      { k: "author", l: "الكاتب", type: "text" },
      { k: "read_time", l: "وقت القراءة", type: "text" },
      { k: "excerpt", l: "مقتطف", type: "textarea" },
      { k: "content", l: "المحتوى (استخدم ## للعناوين و - للنقاط)", type: "textarea", big: true },
      { k: "published", l: "منشور", type: "checkbox" },
    ],
  },
  "case-studies": {
    label: "دراسات الحالة",
    empty: { title: "", client: "", sector: "", cover_image: "", summary: "", problem: "", solution: "", result: "", tags: "", published: true },
    fields: [
      { k: "title", l: "العنوان", type: "text" },
      { k: "client", l: "العميل", type: "text" },
      { k: "sector", l: "القطاع", type: "text" },
      { k: "cover_image", l: "صورة الغلاف", type: "image" },
      { k: "summary", l: "ملخص", type: "textarea" },
      { k: "problem", l: "التحدي", type: "textarea" },
      { k: "solution", l: "الحل", type: "textarea" },
      { k: "result", l: "النتيجة", type: "textarea" },
      { k: "tags", l: "وسوم (افصل بفواصل)", type: "text" },
      { k: "published", l: "منشور", type: "checkbox" },
    ],
  },
  team: {
    label: "الفريق",
    empty: { name: "", role: "", photo: "", bio: "", order: 0 },
    fields: [
      { k: "name", l: "الاسم", type: "text" },
      { k: "role", l: "المنصب", type: "text" },
      { k: "photo", l: "الصورة", type: "image" },
      { k: "bio", l: "نبذة", type: "textarea" },
      { k: "order", l: "الترتيب", type: "number" },
    ],
  },
  testimonials: {
    label: "آراء العملاء",
    empty: { name: "", role: "", company: "", quote: "", avatar: "", rating: 5, order: 0, published: true },
    fields: [
      { k: "name", l: "اسم العميل", type: "text" },
      { k: "role", l: "المنصب", type: "text" },
      { k: "company", l: "الشركة / الجهة", type: "text" },
      { k: "quote", l: "الرأي", type: "textarea" },
      { k: "avatar", l: "الصورة الشخصية", type: "image" },
      { k: "rating", l: "التقييم (1-5)", type: "number" },
      { k: "order", l: "الترتيب", type: "number" },
      { k: "published", l: "منشور", type: "checkbox" },
    ],
  },
  faqs: {
    label: "الأسئلة الشائعة",
    empty: { question: "", answer: "", order: 0, published: true },
    fields: [
      { k: "question", l: "السؤال", type: "text" },
      { k: "answer", l: "الإجابة", type: "textarea", big: true },
      { k: "order", l: "الترتيب", type: "number" },
      { k: "published", l: "منشور", type: "checkbox" },
    ],
  },
  achievements: {
    label: "الإنجازات",
    empty: { label: "", value: "", icon: "trophy", order: 0 },
    fields: [
      { k: "value", l: "القيمة (مثال: +150 أو 98%)", type: "text" },
      { k: "label", l: "الوصف", type: "text" },
      { k: "icon", l: "الأيقونة (rocket / users / calendar / smile / trophy / award)", type: "text" },
      { k: "order", l: "الترتيب", type: "number" },
    ],
  },
  jobs: {
    label: "الوظائف",
    empty: { title: "", type: "دوام كامل", location: "العراق", description: "", requirements: "", order: 0, published: true },
    fields: [
      { k: "title", l: "المسمى الوظيفي", type: "text" },
      { k: "type", l: "نوع الدوام", type: "text" },
      { k: "location", l: "الموقع", type: "text" },
      { k: "description", l: "الوصف", type: "textarea" },
      { k: "requirements", l: "المتطلبات", type: "textarea" },
      { k: "order", l: "الترتيب", type: "number" },
      { k: "published", l: "منشور", type: "checkbox" },
    ],
  },
  milestones: {
    label: "الخط الزمني (المحطّات)",
    empty: { year: "", title: "", description: "", icon: "star", order: 0, published: true },
    fields: [
      { k: "year", l: "السنة", type: "text" },
      { k: "title", l: "عنوان المحطّة", type: "text" },
      { k: "description", l: "الوصف", type: "textarea" },
      { k: "icon", l: "الأيقونة (flag / rocket / users / cpu / briefcase / sparkles / star / award)", type: "text" },
      { k: "order", l: "الترتيب", type: "number" },
      { k: "published", l: "منشور", type: "checkbox" },
    ],
  },
  partners: {
    label: "شعارات العملاء/الشركاء",
    empty: { name: "", logo: "", website: "", order: 0, published: true },
    fields: [
      { k: "name", l: "اسم العميل/الشريك", type: "text" },
      { k: "logo", l: "شعار (URL أو رفع)", type: "image" },
      { k: "website", l: "الرابط (اختياري)", type: "text" },
      { k: "order", l: "الترتيب", type: "number" },
      { k: "published", l: "منشور", type: "checkbox" },
    ],
  },
  certifications: {
    label: "الاعتمادات والشهادات (Trust Badges)",
    empty: { title: "", issuer: "", year: "", logo: "", website: "", order: 0, published: true },
    fields: [
      { k: "title", l: "العنوان (مثل ISO 27001)", type: "text" },
      { k: "issuer", l: "الجهة المُصدرة (مثل ISO / Microsoft)", type: "text" },
      { k: "year", l: "السنة", type: "text" },
      { k: "logo", l: "شعار الشهادة (URL أو رفع)", type: "image" },
      { k: "website", l: "رابط التحقق (اختياري)", type: "text" },
      { k: "order", l: "الترتيب", type: "number" },
      { k: "published", l: "منشور", type: "checkbox" },
    ],
  },
  "video-testimonials": {
    label: "شهادات فيديو (Video Testimonials)",
    empty: { client_name: "", client_role: "", company: "", quote: "", video_url: "", poster: "", duration: "0:30", lang: "ar", order: 0, published: true },
    fields: [
      { k: "client_name", l: "اسم العميل", type: "text" },
      { k: "client_role", l: "المنصب (مثل: المدير التقني)", type: "text" },
      { k: "company", l: "الشركة/الجهة", type: "text" },
      { k: "quote", l: "اقتباس قصير يظهر تحت الفيديو", type: "textarea" },
      { k: "video_url", l: "الفيديو (رابط أو رفع MP4/WebM)", type: "video" },
      { k: "poster", l: "صورة الغلاف (Thumbnail)", type: "image" },
      { k: "duration", l: "المدة (مثل 0:30)", type: "text" },
      { k: "order", l: "الترتيب", type: "number" },
      { k: "published", l: "منشور", type: "checkbox" },
    ],
  },
  applications: {
    label: "طلبات التوظيف (Job Applications)",
    empty: { full_name: "", email: "", phone: "", position: "", cover_letter: "", cv_url: "", linkedin: "", status: "new", lang: "ar" },
    fields: [
      { k: "full_name", l: "اسم المتقدم", type: "text" },
      { k: "email", l: "البريد الإلكتروني", type: "text" },
      { k: "phone", l: "الهاتف", type: "text" },
      { k: "position", l: "المنصب المتقدم له", type: "text" },
      { k: "linkedin", l: "LinkedIn", type: "text" },
      { k: "cover_letter", l: "الرسالة التعريفية", type: "textarea" },
      { k: "cv_url", l: "رابط السيرة الذاتية (PDF)", type: "text" },
      { k: "status", l: "الحالة", type: "select", options: [
        { v: "new", l: "جديد" },
        { v: "reviewing", l: "قيد المراجعة" },
        { v: "interview", l: "مقابلة" },
        { v: "hired", l: "تم التوظيف" },
        { v: "rejected", l: "مرفوض" },
      ] },
    ],
  },
  resources: {
    label: "مركز التحميلات (Resources)",
    empty: { title: "", description: "", category: "profile", file_url: "", cover: "", file_size: "", pages: 0, requires_lead: true, lang: "ar", order: 0, published: true },
    fields: [
      { k: "title", l: "العنوان", type: "text" },
      { k: "description", l: "الوصف", type: "textarea" },
      { k: "category", l: "التصنيف", type: "select", options: [
        { v: "profile", l: "الملف التعريفي" },
        { v: "case-study", l: "دراسة حالة" },
        { v: "guide", l: "دليل تقني" },
      ] },
      { k: "cover", l: "صورة الغلاف", type: "image" },
      { k: "file_url", l: "ملف PDF (رابط أو رفع)", type: "pdf" },
      { k: "file_size", l: "حجم الملف (مثل 2.4 MB)", type: "text" },
      { k: "pages", l: "عدد الصفحات", type: "number" },
      { k: "requires_lead", l: "يتطلب البريد قبل التحميل", type: "checkbox" },
      { k: "order", l: "الترتيب", type: "number" },
      { k: "published", l: "منشور", type: "checkbox" },
    ],
  },
  gallery: {
    label: "معرض الأعمال (Gallery)",
    empty: { title: "", description: "", category: "field", media_type: "image", media_url: "", poster: "", lang: "ar", order: 0, published: true },
    fields: [
      { k: "title", l: "العنوان (اختياري)", type: "text" },
      { k: "description", l: "وصف قصير (اختياري)", type: "textarea" },
      { k: "category", l: "الفئة", type: "select", options: [
        { v: "field", l: "من الميدان" },
        { v: "team", l: "الفريق" },
        { v: "event", l: "فعاليات" },
        { v: "implementation", l: "تنفيذ مشاريع" },
        { v: "office", l: "مقرّاتنا" },
        { v: "other", l: "أخرى" },
      ] },
      { k: "media_type", l: "نوع الوسائط", type: "select", options: [
        { v: "image", l: "صورة" },
        { v: "video", l: "فيديو" },
      ] },
      { k: "media_url", l: "الوسائط (صورة أو فيديو أو YouTube — ارفع أو ألصق رابط)", type: "media" },
      { k: "poster", l: "صورة معاينة (تُستخدم للفيديو)", type: "image" },
      { k: "order", l: "الترتيب", type: "number" },
      { k: "published", l: "منشور", type: "checkbox" },
    ],
  },
};

// Resources that support multilingual content (adds a language selector).
const LANG_RESOURCES = ["blog", "case-studies", "testimonials", "jobs", "milestones", "video-testimonials", "resources", "gallery"];
const LANG_OPTIONS = [["ar", "العربية"], ["ku", "کوردی"], ["en", "English"]];

const inputClass = "w-full rounded-xl bg-[#0d1836] border border-white/10 px-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[color:var(--primary)]";

function ContentForm({ resType, initial, onClose, onSaved, adminKey }) {
  const schema = SCHEMAS[resType];
  const start = { ...schema.empty, ...(initial || {}) };
  if (resType === "case-studies" && Array.isArray(start.tags)) start.tags = start.tags.join(", ");
  if (LANG_RESOURCES.includes(resType) && !start.lang) start.lang = "ar";
  const [form, setForm] = useState(start);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");

  const handleUpload = async (key, file) => {
    if (!file) return;
    // Client-side size pre-check to give instant feedback
    const isVideo = file.type.startsWith("video/");
    const isPdf = file.type === "application/pdf";
    const maxMb = isVideo ? 100 : (isPdf ? 60 : 15);
    if (file.size > maxMb * 1024 * 1024) {
      toast.error(`الملف كبير جدًا (${(file.size / 1024 / 1024).toFixed(1)}MB) — الحد الأقصى ${maxMb}MB`);
      return;
    }
    setUploading(key);
    try {
      const r = await uploadImage(adminKey, file);
      setForm((prev) => ({ ...prev, [key]: r.data.url }));
      toast.success("تم رفع الملف");
    } catch (err) {
      const serverMsg = err?.response?.data?.detail;
      toast.error(serverMsg ? `فشل الرفع: ${serverMsg}` : "فشل رفع الملف");
    } finally {
      setUploading("");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (resType === "case-studies") payload.tags = String(form.tags || "").split(",").map((s) => s.trim()).filter(Boolean);
      if (resType === "team") payload.order = Number(form.order) || 0;
      if (initial?.id) await adminUpdate(adminKey, resType, initial.id, payload);
      else await adminCreate(adminKey, resType, payload);
      toast.success("تم الحفظ بنجاح");
      onSaved();
      onClose();
    } catch (e) {
      toast.error("فشل الحفظ");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-black/70 p-4" onClick={onClose} data-testid="content-form-modal">
      <div className="w-full max-w-2xl max-h-[90vh] flex flex-col card-surface rounded-3xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 pb-4 border-b border-white/10 shrink-0">
          <h3 className="text-xl font-extrabold">{initial?.id ? "تعديل" : "إضافة"} — {schema.label}</h3>
          <button onClick={onClose} className="h-9 w-9 grid place-items-center rounded-full hover:bg-white/10"><X size={18} /></button>
        </div>
        <div className="space-y-4 overflow-y-auto p-6 flex-1">
          {LANG_RESOURCES.includes(resType) && (
            <div>
              <label className="block text-sm text-slate-300 mb-1.5">اللغة</label>
              <select value={form.lang || "ar"} onChange={(e) => setForm({ ...form, lang: e.target.value })} className={inputClass} data-testid="form-lang">
                {LANG_OPTIONS.map(([v, l]) => <option key={v} value={v} className="bg-[#0d1836]">{l}</option>)}
              </select>
            </div>
          )}
          {schema.fields.map((f) => (
            <div key={f.k}>
              {f.type !== "checkbox" && <label className="block text-sm text-slate-300 mb-1.5">{f.l}</label>}
              {f.type === "textarea" ? (
                <textarea value={form[f.k] ?? ""} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} rows={f.big ? 8 : 3} className={`${inputClass} resize-none`} data-testid={`form-${f.k}`} />
              ) : f.type === "checkbox" ? (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={!!form[f.k]} onChange={(e) => setForm({ ...form, [f.k]: e.target.checked })} className="h-4 w-4 accent-[color:var(--primary)]" data-testid={`form-${f.k}`} />
                  <span className="text-sm text-slate-300">{f.l}</span>
                </label>
              ) : f.type === "image" ? (
                <div className="flex items-center gap-3">
                  {form[f.k] ? <img src={toAbsolute(form[f.k])} alt="" className="h-14 w-14 rounded-xl object-cover border border-white/10" /> : <div className="h-14 w-14 rounded-xl bg-[#0d1836] border border-white/10 grid place-items-center text-slate-600"><ImageIcon size={20} /></div>}
                  <input value={form[f.k] ?? ""} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} placeholder="رابط الصورة أو ارفع ملفًا" dir="ltr" className={inputClass} data-testid={`form-${f.k}`} />
                  <label className="shrink-0 rounded-xl border border-white/15 px-3 py-2.5 text-sm font-bold cursor-pointer hover:border-[color:var(--primary)] flex items-center gap-2" data-testid={`upload-${f.k}`}>
                    {uploading === f.k ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handleUpload(f.k, e.target.files?.[0])} />
                  </label>
                </div>
              ) : f.type === "video" ? (
                <div className="flex items-center gap-3">
                  {form[f.k] ? (
                    <div className="h-14 w-14 rounded-xl bg-black border border-white/10 grid place-items-center text-primary">▶</div>
                  ) : (
                    <div className="h-14 w-14 rounded-xl bg-[#0d1836] border border-white/10 grid place-items-center text-slate-600">▶</div>
                  )}
                  <input value={form[f.k] ?? ""} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} placeholder="رابط فيديو أو YouTube أو ارفع ملفًا (MP4/WebM ≤ 100MB)" dir="ltr" className={inputClass} data-testid={`form-${f.k}`} />
                  <label className="shrink-0 rounded-xl border border-white/15 px-3 py-2.5 text-sm font-bold cursor-pointer hover:border-[color:var(--primary)] flex items-center gap-2" data-testid={`upload-${f.k}`}>
                    {uploading === f.k ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                    <input type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden" onChange={(e) => handleUpload(f.k, e.target.files?.[0])} />
                  </label>
                </div>
              ) : f.type === "pdf" ? (
                <div className="flex items-center gap-3">
                  <div className="h-14 w-14 rounded-xl bg-[#0d1836] border border-white/10 grid place-items-center text-primary shrink-0">
                    <FileText size={20} />
                  </div>
                  <input value={form[f.k] ?? ""} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} placeholder="رابط PDF أو ارفع ملفًا (≤ 60MB)" dir="ltr" className={inputClass} data-testid={`form-${f.k}`} />
                  <label className="shrink-0 rounded-xl border border-white/15 px-3 py-2.5 text-sm font-bold cursor-pointer hover:border-[color:var(--primary)] flex items-center gap-2" data-testid={`upload-${f.k}`}>
                    {uploading === f.k ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                    <input type="file" accept="application/pdf" className="hidden" onChange={(e) => handleUpload(f.k, e.target.files?.[0])} />
                  </label>
                </div>
              ) : f.type === "media" ? (
                <div className="flex items-center gap-3">
                  {form[f.k] ? (
                    /\.(mp4|webm|mov)($|\?)/i.test(form[f.k]) || /(youtube|youtu\.be|vimeo)/i.test(form[f.k])
                      ? <div className="h-14 w-14 rounded-xl bg-black border border-white/10 grid place-items-center text-primary">▶</div>
                      : <img src={toAbsolute(form[f.k])} alt="" className="h-14 w-14 rounded-xl object-cover border border-white/10" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                  ) : (
                    <div className="h-14 w-14 rounded-xl bg-[#0d1836] border border-white/10 grid place-items-center text-slate-600"><ImageIcon size={20} /></div>
                  )}
                  <input value={form[f.k] ?? ""} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} placeholder="صورة أو فيديو (MP4/WebM) أو رابط YouTube" dir="ltr" className={inputClass} data-testid={`form-${f.k}`} />
                  <label className="shrink-0 rounded-xl border border-white/15 px-3 py-2.5 text-sm font-bold cursor-pointer hover:border-[color:var(--primary)] flex items-center gap-2" data-testid={`upload-${f.k}`}>
                    {uploading === f.k ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                    <input type="file" accept="image/*,video/mp4,video/webm,video/quicktime" className="hidden" onChange={(e) => handleUpload(f.k, e.target.files?.[0])} />
                  </label>
                </div>
              ) : f.type === "select" ? (
                <select value={form[f.k] ?? ""} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} className={inputClass} data-testid={`form-${f.k}`}>
                  {(f.options || []).map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                </select>
              ) : (
                <input type={f.type} value={form[f.k] ?? ""} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} className={inputClass} data-testid={`form-${f.k}`} />
              )}
            </div>
          ))}
        </div>
        <div className="flex gap-3 p-6 pt-4 border-t border-white/10 shrink-0">
          <button onClick={save} disabled={saving} className="flex-1 rounded-xl px-6 py-3 text-sm font-bold text-[#04120a] disabled:opacity-60" style={{ background: "var(--primary)" }} data-testid="form-save">
            {saving ? <Loader2 size={16} className="animate-spin mx-auto" /> : "حفظ"}
          </button>
          <button onClick={onClose} className="rounded-xl px-6 py-3 text-sm font-bold border border-white/15">إلغاء</button>
        </div>
      </div>
    </div>
  );
}

function ContentManager({ resType, adminKey }) {
  const schema = SCHEMAS[resType];
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminList(adminKey, resType);
      setItems(r.data);
    } catch { /* ignore */ }
    setLoading(false);
  }, [adminKey, resType]);

  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm("هل تريد الحذف؟")) return;
    await adminDelete(adminKey, resType, id);
    toast.success("تم الحذف");
    load();
  };

  const title = (it) => it.title || it.name || it.full_name || it.client_name || it.question || it.label;

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-xl font-extrabold">{schema.label} ({items.length})</h2>
        <button onClick={() => { setEditing(null); setShowForm(true); }} className="flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold text-[#04120a]" style={{ background: "var(--primary)" }} data-testid={`add-${resType}`}>
          <Plus size={16} /> إضافة
        </button>
      </div>
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-primary" /></div>
      ) : (
        <div className="space-y-3">
          {items.map((it) => (
            <div key={it.id} className="card-surface rounded-2xl p-4 flex items-center gap-4" data-testid={`item-${it.id}`}>
              {(it.cover_image || it.photo || it.avatar) && <img src={toAbsolute(it.cover_image || it.photo || it.avatar)} alt="" className="h-14 w-14 rounded-xl object-cover shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="font-bold truncate">{title(it)}</div>
                <div className="text-xs text-slate-500 truncate">{it.category || it.sector || it.role || it.company || it.position || it.email || it.value || ""}{it.status ? ` · ${it.status}` : ""}{it.published === false ? " · مسودة" : ""}</div>
              </div>
              <button onClick={() => { setEditing(it); setShowForm(true); }} className="h-9 w-9 grid place-items-center rounded-full border border-white/10 hover:border-[color:var(--primary)]" data-testid={`edit-${it.id}`}><Pencil size={15} /></button>
              <button onClick={() => del(it.id)} className="h-9 w-9 grid place-items-center rounded-full border border-white/10 hover:border-red-400 text-red-400" data-testid={`delete-${it.id}`}><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      )}
      {showForm && <ContentForm resType={resType} initial={editing} adminKey={adminKey} onClose={() => setShowForm(false)} onSaved={load} />}
    </div>
  );
}

function LeadsManager({ adminKey }) {
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, quotes: 0 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const [c, s] = await Promise.all([fetchContacts(adminKey), fetchStats(adminKey)]);
    setContacts(c.data); setStats(s.data); setLoading(false);
  }, [adminKey]);

  useEffect(() => { load(); }, [load]);

  const markDone = async (id) => { await updateContactStatus(adminKey, id, "done"); load(); };
  const removeLead = async (id) => { if (!window.confirm("حذف الطلب؟")) return; await deleteContact(adminKey, id); load(); };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-xl font-extrabold">الطلبات</h2>
        <button onClick={load} className="flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm hover:border-white/30" data-testid="admin-refresh"><RefreshCw size={15} className={loading ? "animate-spin" : ""} /> تحديث</button>
      </div>
      <div className="grid grid-cols-3 gap-4 mb-6">
        {[{ l: "إجمالي", v: stats.total }, { l: "جديدة", v: stats.new }, { l: "عروض", v: stats.quotes }].map((s, i) => (
          <div key={i} className="card-surface rounded-2xl p-5"><div className="text-3xl font-extrabold text-primary">{s.v}</div><div className="text-sm text-slate-400 mt-1">{s.l}</div></div>
        ))}
      </div>
      {loading ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-primary" /></div> : contacts.length === 0 ? (
        <div className="text-center py-16 text-slate-500"><Inbox size={36} className="mx-auto mb-3" /> لا توجد طلبات بعد</div>
      ) : (
        <div className="space-y-4">
          {contacts.map((c) => (
            <div key={c.id} className="card-surface rounded-2xl p-6" data-testid={`admin-contact-${c.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-bold text-lg">{c.name}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${c.status === "new" ? "bg-[color:var(--primary)] text-[#04120a]" : "bg-white/10 text-slate-300"}`}>{c.status === "new" ? "جديد" : c.status === "done" ? "منجز" : "مقروء"}</span>
                    <span className="text-xs text-slate-500">{new Date(c.created_at).toLocaleString("ar")}</span>
                  </div>
                  <div className="flex flex-wrap gap-4 text-sm text-slate-400 mb-3">
                    <span className="flex items-center gap-1.5" dir="ltr"><Phone size={14} /> {c.phone}</span>
                    {c.email && <span className="flex items-center gap-1.5" dir="ltr"><Mail size={14} /> {c.email}</span>}
                    {c.company && <span className="flex items-center gap-1.5"><Building2 size={14} /> {c.company}</span>}
                  </div>
                  {c.service && <div className="text-sm mb-2"><span className="text-slate-500">الخدمة: </span><span className="text-primary">{c.service}</span></div>}
                  <p className="text-slate-300 text-sm leading-relaxed">{c.message}</p>
                </div>
                <div className="flex items-center gap-2">
                  {c.status !== "done" && <button onClick={() => markDone(c.id)} className="flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm hover:border-[color:var(--primary)]" data-testid={`admin-mark-done-${c.id}`}><CheckCheck size={15} /> إنجاز</button>}
                  <button onClick={() => removeLead(c.id)} className="h-9 w-9 grid place-items-center rounded-full border border-white/10 hover:border-red-400 text-red-400" data-testid={`admin-delete-lead-${c.id}`}><Trash2 size={15} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const TABS = [
  { id: "leads", label: "الطلبات" },
  { id: "content", label: "الهوية والمحتوى" },
  { id: "blog", label: "المدونة" },
  { id: "case-studies", label: "دراسات الحالة" },
  { id: "team", label: "الفريق" },
  { id: "testimonials", label: "آراء العملاء" },
  { id: "faqs", label: "الأسئلة الشائعة" },
  { id: "achievements", label: "الإنجازات" },
  { id: "jobs", label: "الوظائف" },
  { id: "milestones", label: "الخط الزمني" },
  { id: "partners", label: "شعارات العملاء" },
  { id: "certifications", label: "الاعتمادات والشهادات" },
  { id: "video-testimonials", label: "شهادات فيديو" },
  { id: "applications", label: "طلبات التوظيف" },
  { id: "resources", label: "مركز التحميلات" },
  { id: "gallery", label: "معرض الأعمال" },
  { id: "bookings", label: "حجوزات الاستشارات" },
  { id: "tickets", label: "تذاكر الدعم" },
  { id: "newsletter", label: "النشرة البريدية" },
  { id: "settings", label: "الإعدادات" },
];

function NewsletterManager({ adminKey }) {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await fetchSubscribers(adminKey); setSubs(r.data); } catch { /* ignore */ }
    setLoading(false);
  }, [adminKey]);
  useEffect(() => { load(); }, [load]);
  const del = async (id) => { await deleteSubscriber(adminKey, id); load(); };
  return (
    <div>
      <h2 className="text-xl font-extrabold mb-5">المشتركون ({subs.length})</h2>
      {loading ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-primary" /></div> : subs.length === 0 ? (
        <div className="text-center py-16 text-slate-500"><Inbox size={36} className="mx-auto mb-3" /> لا يوجد مشتركون بعد</div>
      ) : (
        <div className="space-y-2">
          {subs.map((s) => (
            <div key={s.id} className="card-surface rounded-xl p-4 flex items-center justify-between gap-4" data-testid={`sub-${s.id}`}>
              <span dir="ltr" className="font-medium">{s.email}</span>
              <div className="flex items-center gap-3">
                <span className="text-xs text-slate-500">{new Date(s.created_at).toLocaleDateString("ar")}</span>
                <button onClick={() => del(s.id)} className="h-8 w-8 grid place-items-center rounded-full border border-white/10 hover:border-red-400 text-red-400" data-testid={`sub-delete-${s.id}`}><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SettingsManager({ adminKey }) {
  const [pdf, setPdf] = useState("");
  const [uploading, setUploading] = useState(false);
  const [mapFields, setMapFields] = useState({
    map_embed_url: "",
    map_directions_url: "",
    address_ar: "",
    address_en: "",
    address_ku: "",
    hours_ar: "",
    hours_en: "",
    hours_ku: "",
    owner_email: "",
    ga_measurement_id: "",
    business_hours_start: "",
    business_hours_end: "",
    business_days: "",
  });
  const [savingMap, setSavingMap] = useState(false);

  useEffect(() => {
    fetchSettings().then((r) => {
      const d = r.data || {};
      setPdf(d.profile_pdf || "");
      setMapFields((prev) => ({
        ...prev,
        map_embed_url: d.map_embed_url || "",
        map_directions_url: d.map_directions_url || "",
        address_ar: d.address_ar || "",
        address_en: d.address_en || "",
        address_ku: d.address_ku || "",
        hours_ar: d.hours_ar || "",
        hours_en: d.hours_en || "",
        hours_ku: d.hours_ku || "",
        owner_email: d.owner_email || "",
        ga_measurement_id: d.ga_measurement_id || "",
        business_hours_start: d.business_hours_start || "",
        business_hours_end: d.business_hours_end || "",
        business_days: d.business_days || "",
      }));
    }).catch(() => {});
  }, []);

  const onUpload = async (file) => {
    if (!file) return;
    // Client-side pre-check: reject clearly wrong types instantly
    if (file.type !== "application/pdf") {
      toast.error("الرجاء رفع ملف PDF فقط");
      return;
    }
    if (file.size > 60 * 1024 * 1024) {
      toast.error(`الملف كبير جدًا (${(file.size / 1024 / 1024).toFixed(1)}MB) — الحد الأقصى 60MB`);
      return;
    }
    setUploading(true);
    try {
      const r = await uploadImage(adminKey, file);
      await updateSettings(adminKey, { profile_pdf: r.data.url });
      setPdf(r.data.url);
      toast.success("تم رفع الملف التعريفي");
    } catch (err) {
      const serverMsg = err?.response?.data?.detail;
      toast.error(serverMsg ? `فشل الرفع: ${serverMsg}` : "فشل الرفع (تأكد من الاتصال بالإنترنت)");
    }
    finally { setUploading(false); }
  };
  const remove = async () => { await updateSettings(adminKey, { profile_pdf: "" }); setPdf(""); toast.success("تم الحذف"); };

  const onMapChange = (k, v) => setMapFields((f) => ({ ...f, [k]: v }));
  const saveMap = async () => {
    setSavingMap(true);
    try {
      await updateSettings(adminKey, mapFields);
      toast.success("تم حفظ إعدادات الخريطة");
    } catch { toast.error("فشل حفظ إعدادات الخريطة"); }
    finally { setSavingMap(false); }
  };

  const inputCls = "w-full rounded-xl bg-[#0d1836] border border-white/10 px-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[color:var(--primary)]";

  const mapRows = [
    { k: "map_embed_url", l: "رابط تضمين الخريطة (Google Maps embed src)", ph: "https://www.google.com/maps?q=...&output=embed", dir: "ltr" },
    { k: "map_directions_url", l: "رابط الاتجاهات (Directions URL)", ph: "https://www.google.com/maps/dir/?api=1&destination=...", dir: "ltr" },
    { k: "address_ar", l: "العنوان (عربي)", ph: "بغداد، المنصور، شارع 14 رمضان" },
    { k: "address_en", l: "Address (English)", ph: "Baghdad, Al-Mansour, 14 Ramadan St.", dir: "ltr" },
    { k: "address_ku", l: "ناونیشان (کوردی)", ph: "بەغدا، مەنسور، شەقامی 14 ی ڕەمەزان" },
    { k: "hours_ar", l: "ساعات العمل (عربي)", ph: "الأحد – الخميس: 9 ص – 5 م" },
    { k: "hours_en", l: "Working Hours (English)", ph: "Sun – Thu: 9 AM – 5 PM", dir: "ltr" },
    { k: "hours_ku", l: "کاتەکانی کار (کوردی)", ph: "یەکشەممە – پێنجشەممە: 9 – 5" },
    { k: "owner_email", l: "البريد المستلم لإشعارات الطلبات (Lead notifications)", ph: "owner@yourcompany.com", dir: "ltr" },
    { k: "ga_measurement_id", l: "Google Analytics 4 — Measurement ID", ph: "G-XXXXXXXXXX", dir: "ltr" },
    { k: "business_hours_start", l: "بداية الدوام (HH:MM بتوقيت بغداد)", ph: "09:00", dir: "ltr" },
    { k: "business_hours_end", l: "نهاية الدوام (HH:MM بتوقيت بغداد)", ph: "17:00", dir: "ltr" },
    { k: "business_days", l: "أيام العمل (0=أحد, 1=إثنين,... 6=سبت)", ph: "6,0,1,2,3,4", dir: "ltr" },
  ];

  return (
    <div className="space-y-8">
      <h2 className="text-xl font-extrabold">إعدادات الموقع</h2>

      <div className="card-surface rounded-2xl p-6 max-w-lg">
        <h3 className="font-bold mb-2">الملف التعريفي للشركة (PDF)</h3>
        <p className="text-sm text-slate-400 mb-4">يظهر كزر تحميل في تذييل الموقع.</p>
        {pdf ? (
          <div className="flex items-center gap-3">
            <a href={toAbsolute(pdf)} target="_blank" rel="noopener noreferrer" className="rounded-full px-5 py-2.5 text-sm font-bold text-[#04120a]" style={{ background: "var(--primary)" }} data-testid="settings-pdf-link">عرض الملف الحالي</a>
            <button onClick={remove} className="rounded-full px-4 py-2.5 text-sm border border-white/15 hover:border-red-400 text-red-400" data-testid="settings-pdf-remove">حذف</button>
          </div>
        ) : (
          <label className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm font-bold cursor-pointer hover:border-[color:var(--primary)]" data-testid="settings-pdf-upload">
            {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} رفع PDF
            <input type="file" accept="application/pdf" className="hidden" onChange={(e) => onUpload(e.target.files?.[0])} />
          </label>
        )}
      </div>

      <div className="card-surface rounded-2xl p-6 max-w-3xl" data-testid="settings-map-card">
        <h3 className="font-bold mb-1">إعدادات الخريطة والعنوان</h3>
        <p className="text-sm text-slate-400 mb-2">تُستخدم هذه القيم في قسم &quot;زُرنا في مكاتبنا&quot; وفي تذييل الموقع.</p>
        <p className="text-xs text-slate-500 mb-5">
          للحصول على رابط التضمين: افتح Google Maps → مشاركة → تضمين خريطة → انسخ قيمة <code className="text-primary">src</code> من الـ iframe.
          اترك الحقل فارغًا لاستخدام الموقع الافتراضي (بغداد).
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          {mapRows.map((r) => (
            <div key={r.k} className={["map_embed_url", "map_directions_url", "owner_email", "ga_measurement_id"].includes(r.k) ? "sm:col-span-2" : ""}>
              <label className="block text-sm text-slate-300 mb-1.5">{r.l}</label>
              <input
                type="text"
                value={mapFields[r.k]}
                onChange={(e) => onMapChange(r.k, e.target.value)}
                placeholder={r.ph}
                dir={r.dir || undefined}
                className={inputCls}
                data-testid={`settings-map-${r.k}`}
              />
            </div>
          ))}
        </div>
        <button
          onClick={saveMap}
          disabled={savingMap}
          className="mt-6 inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-bold text-[#04120a] disabled:opacity-60"
          style={{ background: "var(--primary)" }}
          data-testid="settings-map-save"
        >
          {savingMap ? <><Loader2 size={16} className="animate-spin" /> جارٍ الحفظ...</> : "حفظ إعدادات الخريطة"}
        </button>

        <PreviewEmailButton adminKey={adminKey} />
      </div>

      <ProjectVideosCard adminKey={adminKey} />
    </div>
  );
}

function PreviewEmailButton({ adminKey }) {
  const [loading, setLoading] = useState(false);
  const send = async () => {
    setLoading(true);
    try {
      const r = await previewEmail(adminKey);
      toast.success(`تم إرسال بريد المعاينة إلى ${r.data.sent_to}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "فشل إرسال المعاينة";
      toast.error(msg);
    } finally { setLoading(false); }
  };
  return (
    <button
      onClick={send}
      disabled={loading}
      className="mt-3 ms-3 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold border border-white/15 hover:border-[color:var(--primary)] disabled:opacity-60"
      data-testid="settings-preview-email"
      type="button"
    >
      {loading ? <><Loader2 size={16} className="animate-spin" /> جارٍ الإرسال...</> : <><Mail size={15} /> إرسال بريد معاينة</>}
    </button>
  );
}

// =============== BOOKINGS MANAGER ===============
function BookingsManager({ adminKey }) {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("");

  const load = () => {
    adminListBookings(adminKey).then((r) => setItems(r.data)).catch(() => setItems([]));
  };
  useEffect(load, [adminKey]);

  const update = async (id, patch) => {
    await adminUpdateBooking(adminKey, id, patch);
    toast.success("تم التحديث");
    load();
  };
  const remove = async (id) => {
    if (!window.confirm("حذف هذا الحجز؟")) return;
    await adminDeleteBooking(adminKey, id);
    toast.success("تم الحذف");
    load();
  };

  const filtered = items ? items.filter((b) => (filter ? b.status === filter : true)) : null;
  const statusBadge = (s) => ({
    pending: { bg: "rgba(250,204,21,0.15)", color: "#facc15", l: "قيد الانتظار" },
    confirmed: { bg: "rgba(0,242,96,0.15)", color: "var(--primary)", l: "مؤكّد" },
    completed: { bg: "rgba(148,163,184,0.15)", color: "#94a3b8", l: "منتهي" },
    cancelled: { bg: "rgba(239,68,68,0.15)", color: "#ef4444", l: "ملغى" },
  }[s] || { bg: "rgba(148,163,184,0.15)", color: "#94a3b8", l: s });

  return (
    <div className="space-y-5" data-testid="bookings-manager">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-xl font-extrabold">حجوزات الاستشارات ({filtered?.length ?? 0})</h2>
        <div className="flex flex-wrap gap-2">
          {[
            { k: "", l: "الكل" },
            { k: "pending", l: "قيد الانتظار" },
            { k: "confirmed", l: "مؤكّد" },
            { k: "completed", l: "منتهي" },
            { k: "cancelled", l: "ملغى" },
          ].map(({ k, l }) => (
            <button key={k} onClick={() => setFilter(k)} className={`rounded-full px-3 py-1.5 text-xs font-bold border ${filter === k ? "text-[#04120a] border-transparent" : "border-white/10 text-slate-300"}`} style={filter === k ? { background: "var(--primary)" } : {}} data-testid={`bookings-filter-${k || "all"}`}>{l}</button>
          ))}
        </div>
      </div>
      {filtered === null ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-primary" /></div>
      ) : filtered.length === 0 ? (
        <p className="text-center text-slate-500 py-16">لا توجد حجوزات {filter ? "بهذه الحالة" : ""}.</p>
      ) : (
        <div className="grid gap-3">
          {filtered.map((b) => {
            const bd = statusBadge(b.status);
            return (
              <div key={b.id} className="card-surface rounded-2xl p-5 flex flex-col md:flex-row gap-4 md:items-center justify-between" data-testid={`booking-row-${b.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <span className="rounded-full px-3 py-1 text-xs font-bold" style={{ background: bd.bg, color: bd.color }}>{bd.l}</span>
                    <span className="text-sm text-slate-400" dir="ltr">{b.date} • {b.time}</span>
                  </div>
                  <div className="font-extrabold">{b.name}</div>
                  <div className="text-sm text-slate-400" dir="ltr">📞 {b.phone} {b.email && <>• ✉️ {b.email}</>}</div>
                  {b.topic && <div className="text-sm mt-1"><b>الموضوع:</b> {b.topic}</div>}
                  {b.notes && <div className="text-sm text-slate-400 mt-1 whitespace-pre-line">{b.notes}</div>}
                </div>
                <div className="flex flex-wrap items-center gap-2 shrink-0">
                  <select value={b.status} onChange={(e) => update(b.id, { status: e.target.value })} className="rounded-lg bg-[#0d1836] border border-white/10 px-3 py-1.5 text-sm" data-testid={`booking-status-${b.id}`}>
                    <option value="pending">قيد الانتظار</option>
                    <option value="confirmed">مؤكّد</option>
                    <option value="completed">منتهي</option>
                    <option value="cancelled">ملغى</option>
                  </select>
                  <button onClick={() => remove(b.id)} className="rounded-lg px-3 py-1.5 text-xs font-bold border border-white/10 hover:border-red-400 text-red-400" data-testid={`booking-delete-${b.id}`}>حذف</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


// =============== TICKETS MANAGER ===============
function TicketsManager({ adminKey }) {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("");
  const load = () => adminListTickets(adminKey).then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(load, [adminKey]);
  const update = async (id, patch) => { await adminUpdateTicket(adminKey, id, patch); toast.success("تم التحديث"); load(); };
  const remove = async (id) => { if (!window.confirm("حذف؟")) return; await adminDeleteTicket(adminKey, id); toast.success("تم الحذف"); load(); };
  const filtered = items ? items.filter((tk) => (filter ? tk.status === filter : true)) : null;
  const prioBadge = (p) => ({ urgent: { c: "#ef4444", l: "عاجل" }, high: { c: "#f59e0b", l: "مرتفع" }, normal: { c: "var(--primary)", l: "عادي" }, low: { c: "#94a3b8", l: "منخفض" } }[p] || { c: "#94a3b8", l: p });

  return (
    <div className="space-y-5" data-testid="tickets-manager">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-xl font-extrabold">تذاكر الدعم ({filtered?.length ?? 0})</h2>
        <div className="flex flex-wrap gap-2">
          {[["","الكل"],["open","مفتوحة"],["in_progress","قيد المعالجة"],["resolved","محلولة"],["closed","مغلقة"]].map(([k,l]) => (
            <button key={k} onClick={() => setFilter(k)} className={`rounded-full px-3 py-1.5 text-xs font-bold border ${filter === k ? "text-[#04120a] border-transparent" : "border-white/10 text-slate-300"}`} style={filter === k ? { background: "var(--primary)" } : {}} data-testid={`tickets-filter-${k || "all"}`}>{l}</button>
          ))}
        </div>
      </div>
      {filtered === null ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-primary" /></div>
      : filtered.length === 0 ? <p className="text-center text-slate-500 py-16">لا توجد تذاكر.</p>
      : (
        <div className="grid gap-3">
          {filtered.map((tk) => {
            const p = prioBadge(tk.priority);
            return (
              <div key={tk.id} className="card-surface rounded-2xl p-5" data-testid={`ticket-row-${tk.id}`}>
                <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="rounded-full px-3 py-0.5 text-xs font-bold" style={{ background: p.c, color: "#04120a" }}>{p.l}</span>
                      <span className="text-xs text-slate-400" dir="ltr">{tk.created_at?.slice(0,16).replace('T',' ')}</span>
                    </div>
                    <div className="font-extrabold text-lg">{tk.subject}</div>
                    <div className="text-sm text-slate-400"><b>{tk.name}</b> — <span dir="ltr">{tk.email}</span> {tk.phone && <>• <span dir="ltr">{tk.phone}</span></>}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <select value={tk.status} onChange={(e) => update(tk.id, { status: e.target.value })} className="rounded-lg bg-[#0d1836] border border-white/10 px-3 py-1.5 text-sm" data-testid={`ticket-status-${tk.id}`}>
                      <option value="open">مفتوحة</option>
                      <option value="in_progress">قيد المعالجة</option>
                      <option value="resolved">محلولة</option>
                      <option value="closed">مغلقة</option>
                    </select>
                    <button onClick={() => remove(tk.id)} className="rounded-lg px-3 py-1.5 text-xs font-bold border border-white/10 hover:border-red-400 text-red-400" data-testid={`ticket-delete-${tk.id}`}>حذف</button>
                  </div>
                </div>
                <div className="rounded-xl bg-[#0a1230] border border-white/10 p-4 text-sm text-slate-300 whitespace-pre-line">{tk.message}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// =============== SITE CONTENT EDITOR (Identity / Contact / Hero / About / Intro) ===============
const CONTENT_SECTIONS = [
  {
    id: "identity",
    label: "🏷️ الهوية",
    hint: "اسم الشركة وتاغلاين — يظهر في العنوان والفوتر.",
    fields: [
      { k: "identity_name_ar", l: "اسم الشركة (عربي)" },
      { k: "identity_name_en", l: "Company name (English)", dir: "ltr" },
      { k: "identity_name_ku", l: "ناوی کۆمپانیا (کوردی)" },
      { k: "identity_tagline_ar", l: "التاغلاين (عربي)" },
      { k: "identity_tagline_en", l: "Tagline (English)", dir: "ltr" },
      { k: "identity_tagline_ku", l: "دروشم (کوردی)" },
    ],
  },
  {
    id: "contact",
    label: "📞 بيانات التواصل",
    hint: "الهاتف، الواتساب، البريد، الموقع، وروابط السوشيال — تظهر في كل الصفحات.",
    fields: [
      { k: "contact_phone", l: "الهاتف", ph: "07826699000", dir: "ltr" },
      { k: "contact_whatsapp", l: "واتساب (مع رمز الدولة، بدون +)", ph: "9647826699000", dir: "ltr" },
      { k: "contact_email", l: "البريد الإلكتروني", ph: "madenati@dlr.iq", dir: "ltr" },
      { k: "contact_website", l: "الموقع الإلكتروني", ph: "www.madenati.dlr.iq", dir: "ltr" },
      { k: "social_facebook", l: "Facebook URL", dir: "ltr" },
      { k: "social_instagram", l: "Instagram URL", dir: "ltr" },
      { k: "social_linkedin", l: "LinkedIn URL", dir: "ltr" },
      { k: "social_tiktok", l: "TikTok URL", dir: "ltr" },
    ],
  },
  {
    id: "hero",
    label: "🚀 القسم الرئيسي (Hero)",
    hint: "أول ما يراه الزائر: الشارة، عنوان السطرين، الوصف، الفيديو، والإحصائيات الثلاث.",
    fields: [
      { k: "hero_badge_ar", l: "الشارة (عربي)", ph: "شركة عراقية للتحول الرقمي" },
      { k: "hero_badge_en", l: "Badge (English)", dir: "ltr" },
      { k: "hero_badge_ku", l: "بادج (کوردی)" },
      { k: "hero_title_line1_ar", l: "العنوان — السطر 1 (عربي)", ph: "حلول ذكية" },
      { k: "hero_title_line1_en", l: "Title line 1 (English)", dir: "ltr" },
      { k: "hero_title_line1_ku", l: "دێڕی 1 (کوردی)" },
      { k: "hero_title_line2_ar", l: "العنوان — السطر 2 (عربي، يظهر بالأخضر)", ph: "لمستقبل أفضل" },
      { k: "hero_title_line2_en", l: "Title line 2 (English)", dir: "ltr" },
      { k: "hero_title_line2_ku", l: "دێڕی 2 (کوردی)" },
      { k: "hero_subtitle_ar", l: "الوصف (عربي)", type: "textarea" },
      { k: "hero_subtitle_en", l: "Subtitle (English)", type: "textarea", dir: "ltr" },
      { k: "hero_subtitle_ku", l: "کورتە (کوردی)", type: "textarea" },
      { k: "hero_video_url", l: "فيديو الخلفية (MP4/WebM URL)", dir: "ltr", uploadKind: "video" },
      { k: "hero_poster_url", l: "صورة الغلاف (Poster URL)", dir: "ltr", uploadKind: "image" },
      { k: "hero_stat1_num", l: "الإحصائية 1 — الرقم", ph: "9+", dir: "ltr" },
      { k: "hero_stat1_label_ar", l: "الإحصائية 1 — العنوان (عربي)" },
      { k: "hero_stat1_label_en", l: "Stat 1 label (English)", dir: "ltr" },
      { k: "hero_stat1_label_ku", l: "ناوی 1 (کوردی)" },
      { k: "hero_stat2_num", l: "الإحصائية 2 — الرقم", ph: "10+", dir: "ltr" },
      { k: "hero_stat2_label_ar", l: "الإحصائية 2 — العنوان (عربي)" },
      { k: "hero_stat2_label_en", l: "Stat 2 label (English)", dir: "ltr" },
      { k: "hero_stat2_label_ku", l: "ناوی 2 (کوردی)" },
      { k: "hero_stat3_num", l: "الإحصائية 3 — الرقم", ph: "24/7", dir: "ltr" },
      { k: "hero_stat3_label_ar", l: "الإحصائية 3 — العنوان (عربي)" },
      { k: "hero_stat3_label_en", l: "Stat 3 label (English)", dir: "ltr" },
      { k: "hero_stat3_label_ku", l: "ناوی 3 (کوردی)" },
    ],
  },
  {
    id: "impact",
    label: "📊 عدّاد الإنجازات (Impact Stats)",
    hint: "شريط الأرقام المتحرّك بين الهيرو و 'من نحن'. اترك أي حقل فارغًا لاستخدام القيم الافتراضية.",
    fields: [
      { k: "impact_tag_ar", l: "الشارة (عربي)", ph: "أرقام تصنع الفارق" },
      { k: "impact_tag_en", l: "Tag (English)", dir: "ltr", ph: "Numbers That Matter" },
      { k: "impact_tag_ku", l: "بادج (کوردی)" },
      { k: "impact_title_ar", l: "العنوان (عربي)", ph: "أثرنا بالأرقام" },
      { k: "impact_title_en", l: "Title (English)", dir: "ltr", ph: "Our impact in numbers" },
      { k: "impact_title_ku", l: "سەردێڕ (کوردی)" },
      { k: "impact_subtitle_ar", l: "الوصف (عربي)", type: "textarea" },
      { k: "impact_subtitle_en", l: "Subtitle (English)", type: "textarea", dir: "ltr" },
      { k: "impact_subtitle_ku", l: "کورتە (کوردی)", type: "textarea" },
      { k: "impact_stat1_num", l: "الإحصائية 1 — الرقم", ph: "150+", dir: "ltr" },
      { k: "impact_stat1_label_ar", l: "الإحصائية 1 — التسمية (عربي)", ph: "مشروع مُنجز" },
      { k: "impact_stat1_label_en", l: "Stat 1 label (English)", dir: "ltr" },
      { k: "impact_stat1_label_ku", l: "ناوی 1 (کوردی)" },
      { k: "impact_stat2_num", l: "الإحصائية 2 — الرقم", ph: "80+", dir: "ltr" },
      { k: "impact_stat2_label_ar", l: "الإحصائية 2 — التسمية (عربي)", ph: "عميل يثق بنا" },
      { k: "impact_stat2_label_en", l: "Stat 2 label (English)", dir: "ltr" },
      { k: "impact_stat2_label_ku", l: "ناوی 2 (کوردی)" },
      { k: "impact_stat3_num", l: "الإحصائية 3 — الرقم", ph: "12", dir: "ltr" },
      { k: "impact_stat3_label_ar", l: "الإحصائية 3 — التسمية (عربي)", ph: "سنة خبرة" },
      { k: "impact_stat3_label_en", l: "Stat 3 label (English)", dir: "ltr" },
      { k: "impact_stat3_label_ku", l: "ناوی 3 (کوردی)" },
      { k: "impact_stat4_num", l: "الإحصائية 4 — الرقم", ph: "24/7", dir: "ltr" },
      { k: "impact_stat4_label_ar", l: "الإحصائية 4 — التسمية (عربي)", ph: "دعم متواصل" },
      { k: "impact_stat4_label_en", l: "Stat 4 label (English)", dir: "ltr" },
      { k: "impact_stat4_label_ku", l: "ناوی 4 (کوردی)" },
    ],
  },
  {
    id: "trust",
    label: "🏅 عناوين الاعتمادات (Trust Badges)",
    hint: "نصوص قسم الاعتمادات — البطاقات نفسها تُدار من تبويب 'الاعتمادات والشهادات'.",
    fields: [
      { k: "trust_tag_ar", l: "الشارة (عربي)", ph: "الاعتمادات والشراكات" },
      { k: "trust_tag_en", l: "Tag (English)", dir: "ltr", ph: "Certifications & Partnerships" },
      { k: "trust_tag_ku", l: "بادج (کوردی)" },
      { k: "trust_title_ar", l: "العنوان (عربي)", ph: "معتمدون ومعترف بنا" },
      { k: "trust_title_en", l: "Title (English)", dir: "ltr", ph: "Certified & Recognized" },
      { k: "trust_title_ku", l: "سەردێڕ (کوردی)" },
      { k: "trust_subtitle_ar", l: "الوصف (عربي)", type: "textarea" },
      { k: "trust_subtitle_en", l: "Subtitle (English)", type: "textarea", dir: "ltr" },
      { k: "trust_subtitle_ku", l: "کورتە (کوردی)", type: "textarea" },
    ],
  },
  {
    id: "video-testimonials",
    label: "🎥 عناوين شهادات الفيديو",
    hint: "نصوص قسم شهادات الفيديو — الفيديوهات نفسها تُدار من تبويب 'شهادات فيديو'.",
    fields: [
      { k: "vidtest_tag_ar", l: "الشارة (عربي)", ph: "شهادات عملائنا" },
      { k: "vidtest_tag_en", l: "Tag (English)", dir: "ltr", ph: "Client Testimonials" },
      { k: "vidtest_tag_ku", l: "بادج (کوردی)" },
      { k: "vidtest_title_ar", l: "العنوان (عربي)", ph: "قصص نجاح حقيقية" },
      { k: "vidtest_title_en", l: "Title (English)", dir: "ltr", ph: "Real success stories" },
      { k: "vidtest_title_ku", l: "سەردێڕ (کوردی)" },
      { k: "vidtest_subtitle_ar", l: "الوصف (عربي)", type: "textarea" },
      { k: "vidtest_subtitle_en", l: "Subtitle (English)", type: "textarea", dir: "ltr" },
      { k: "vidtest_subtitle_ku", l: "کورتە (کوردی)", type: "textarea" },
    ],
  },
  {
    id: "pricing",
    label: "💰 الباقات والأسعار (Pricing)",
    hint: "خطط الأسعار على الصفحة الرئيسية. لكل ميزة: استخدم + في بدايتها إن كانت مشمولة، أو - إن لم تكن.",
    fields: [
      { k: "pricing_tag_ar", l: "الشارة (عربي)", ph: "الباقات والأسعار" },
      { k: "pricing_tag_en", l: "Tag (English)", dir: "ltr", ph: "Pricing & Plans" },
      { k: "pricing_tag_ku", l: "بادج (کوردی)" },
      { k: "pricing_title_ar", l: "العنوان (عربي)", ph: "خطط تناسب كل احتياج" },
      { k: "pricing_title_en", l: "Title (English)", dir: "ltr" },
      { k: "pricing_title_ku", l: "سەردێڕ (کوردی)" },
      { k: "pricing_subtitle_ar", l: "الوصف (عربي)", type: "textarea" },
      { k: "pricing_subtitle_en", l: "Subtitle (English)", type: "textarea", dir: "ltr" },
      { k: "pricing_subtitle_ku", l: "کورتە (کوردی)", type: "textarea" },
      // Plan 1
      { k: "pricing_plan1_name_ar", l: "الباقة 1 — الاسم (عربي)", ph: "Basic" },
      { k: "pricing_plan1_name_en", l: "Plan 1 — Name (English)", dir: "ltr" },
      { k: "pricing_plan1_name_ku", l: "پاکێج 1 — ناو" },
      { k: "pricing_plan1_price", l: "الباقة 1 — السعر", ph: "1,500", dir: "ltr" },
      { k: "pricing_plan1_period_ar", l: "الباقة 1 — الفترة (عربي)", ph: "من / يبدأ" },
      { k: "pricing_plan1_period_en", l: "Plan 1 — Period (English)", dir: "ltr" },
      { k: "pricing_plan1_period_ku", l: "پاکێج 1 — ماوە" },
      { k: "pricing_plan1_desc_ar", l: "الباقة 1 — الوصف (عربي)", type: "textarea" },
      { k: "pricing_plan1_desc_en", l: "Plan 1 — Desc (English)", type: "textarea", dir: "ltr" },
      { k: "pricing_plan1_desc_ku", l: "پاکێج 1 — کورتە", type: "textarea" },
      { k: "pricing_plan1_features_ar", l: "الباقة 1 — الميزات (عربي — سطر لكل ميزة، ابدأ بـ + أو -)", type: "textarea" },
      { k: "pricing_plan1_features_en", l: "Plan 1 — Features (English)", type: "textarea", dir: "ltr" },
      { k: "pricing_plan1_features_ku", l: "پاکێج 1 — تایبەتمەندییەکان", type: "textarea" },
      { k: "pricing_plan1_popular", l: "الباقة 1 — الأكثر طلبًا (true/false)", ph: "false", dir: "ltr" },
      // Plan 2
      { k: "pricing_plan2_name_ar", l: "الباقة 2 — الاسم (عربي)", ph: "Professional" },
      { k: "pricing_plan2_name_en", l: "Plan 2 — Name (English)", dir: "ltr" },
      { k: "pricing_plan2_name_ku", l: "پاکێج 2 — ناو" },
      { k: "pricing_plan2_price", l: "الباقة 2 — السعر", ph: "5,000", dir: "ltr" },
      { k: "pricing_plan2_period_ar", l: "الباقة 2 — الفترة (عربي)" },
      { k: "pricing_plan2_period_en", l: "Plan 2 — Period (English)", dir: "ltr" },
      { k: "pricing_plan2_period_ku", l: "پاکێج 2 — ماوە" },
      { k: "pricing_plan2_desc_ar", l: "الباقة 2 — الوصف (عربي)", type: "textarea" },
      { k: "pricing_plan2_desc_en", l: "Plan 2 — Desc (English)", type: "textarea", dir: "ltr" },
      { k: "pricing_plan2_desc_ku", l: "پاکێج 2 — کورتە", type: "textarea" },
      { k: "pricing_plan2_features_ar", l: "الباقة 2 — الميزات (عربي)", type: "textarea" },
      { k: "pricing_plan2_features_en", l: "Plan 2 — Features (English)", type: "textarea", dir: "ltr" },
      { k: "pricing_plan2_features_ku", l: "پاکێج 2 — تایبەتمەندییەکان", type: "textarea" },
      { k: "pricing_plan2_popular", l: "الباقة 2 — الأكثر طلبًا (true/false)", ph: "true", dir: "ltr" },
      // Plan 3
      { k: "pricing_plan3_name_ar", l: "الباقة 3 — الاسم (عربي)", ph: "Enterprise" },
      { k: "pricing_plan3_name_en", l: "Plan 3 — Name (English)", dir: "ltr" },
      { k: "pricing_plan3_name_ku", l: "پاکێج 3 — ناو" },
      { k: "pricing_plan3_price", l: "الباقة 3 — السعر", ph: "حسب الطلب", dir: "ltr" },
      { k: "pricing_plan3_period_ar", l: "الباقة 3 — الفترة (عربي)" },
      { k: "pricing_plan3_period_en", l: "Plan 3 — Period (English)", dir: "ltr" },
      { k: "pricing_plan3_period_ku", l: "پاکێج 3 — ماوە" },
      { k: "pricing_plan3_desc_ar", l: "الباقة 3 — الوصف (عربي)", type: "textarea" },
      { k: "pricing_plan3_desc_en", l: "Plan 3 — Desc (English)", type: "textarea", dir: "ltr" },
      { k: "pricing_plan3_desc_ku", l: "پاکێج 3 — کورتە", type: "textarea" },
      { k: "pricing_plan3_features_ar", l: "الباقة 3 — الميزات (عربي)", type: "textarea" },
      { k: "pricing_plan3_features_en", l: "Plan 3 — Features (English)", type: "textarea", dir: "ltr" },
      { k: "pricing_plan3_features_ku", l: "پاکێج 3 — تایبەتمەندییەکان", type: "textarea" },
      { k: "pricing_plan3_popular", l: "الباقة 3 — الأكثر طلبًا (true/false)", ph: "false", dir: "ltr" },
    ],
  },
  {
    id: "about",
    label: "💡 من نحن",
    hint: "نص التعريف بالشركة، الرؤية، والرسالة.",
    fields: [
      { k: "about_title_ar", l: "العنوان (عربي)" },
      { k: "about_title_en", l: "Title (English)", dir: "ltr" },
      { k: "about_title_ku", l: "سەردێڕ (کوردی)" },
      { k: "about_body_ar", l: "النص (عربي)", type: "textarea" },
      { k: "about_body_en", l: "Body (English)", type: "textarea", dir: "ltr" },
      { k: "about_body_ku", l: "دەق (کوردی)", type: "textarea" },
      { k: "about_vision_title_ar", l: "عنوان الرؤية (عربي)" },
      { k: "about_vision_title_en", l: "Vision title (English)", dir: "ltr" },
      { k: "about_vision_title_ku", l: "سەردێڕی دیدگا (کوردی)" },
      { k: "about_vision_body_ar", l: "نص الرؤية (عربي)", type: "textarea" },
      { k: "about_vision_body_en", l: "Vision body (English)", type: "textarea", dir: "ltr" },
      { k: "about_vision_body_ku", l: "دەقی دیدگا (کوردی)", type: "textarea" },
      { k: "about_mission_title_ar", l: "عنوان الرسالة (عربي)" },
      { k: "about_mission_title_en", l: "Mission title (English)", dir: "ltr" },
      { k: "about_mission_title_ku", l: "سەردێڕی ئەرک (کوردی)" },
      { k: "about_mission_body_ar", l: "نص الرسالة (عربي)", type: "textarea" },
      { k: "about_mission_body_en", l: "Mission body (English)", type: "textarea", dir: "ltr" },
      { k: "about_mission_body_ku", l: "دەقی ئەرک (کوردی)", type: "textarea" },
    ],
  },
  {
    id: "intro",
    label: "🎬 الفيديو التعريفي",
    hint: "الفيديو التعريفي الذي يظهر بعد قسم 'من نحن'.",
    fields: [
      { k: "intro_tag_ar", l: "الشارة (عربي)" },
      { k: "intro_tag_en", l: "Tag (English)", dir: "ltr" },
      { k: "intro_tag_ku", l: "تاگ (کوردی)" },
      { k: "intro_title_ar", l: "العنوان (عربي)" },
      { k: "intro_title_en", l: "Title (English)", dir: "ltr" },
      { k: "intro_title_ku", l: "سەردێڕ (کوردی)" },
      { k: "intro_subtitle_ar", l: "الوصف (عربي)", type: "textarea" },
      { k: "intro_subtitle_en", l: "Subtitle (English)", type: "textarea", dir: "ltr" },
      { k: "intro_subtitle_ku", l: "کورتە (کوردی)", type: "textarea" },
      { k: "intro_video_url", l: "رابط الفيديو (MP4/WebM)", dir: "ltr", uploadKind: "video" },
      { k: "intro_poster_url", l: "صورة الغلاف", dir: "ltr", uploadKind: "image" },
    ],
  },
  {
    id: "services",
    label: "🛠️ الخدمات (9 عناصر)",
    hint: "عناوين وأوصاف الخدمات — نص + صورة لكل خدمة (بثلاث لغات).",
    fields: (() => {
      const rows = [];
      for (let i = 0; i < 9; i++) {
        const n = i + 1;
        rows.push({ k: `service_${i}_title_ar`, l: `الخدمة ${n} — العنوان (عربي)` });
        rows.push({ k: `service_${i}_title_en`, l: `Service ${n} — Title (English)`, dir: "ltr" });
        rows.push({ k: `service_${i}_title_ku`, l: `خزمەت ${n} — سەردێڕ (کوردی)` });
        rows.push({ k: `service_${i}_desc_ar`, l: `الخدمة ${n} — الوصف (عربي)`, type: "textarea" });
        rows.push({ k: `service_${i}_desc_en`, l: `Service ${n} — Description (English)`, type: "textarea", dir: "ltr" });
        rows.push({ k: `service_${i}_desc_ku`, l: `خزمەت ${n} — پێناسە (کوردی)`, type: "textarea" });
        rows.push({ k: `service_${i}_image`, l: `الخدمة ${n} — صورة (اختياري)`, dir: "ltr", uploadKind: "image" });
      }
      return rows;
    })(),
  },
  {
    id: "projects",
    label: "🎬 المشاريع (8 مشاريع)",
    hint: "عناوين ومزايا وصور المشاريع (فيديو المشروع يُعدَّل من تبويب الإعدادات).",
    fields: (() => {
      const rows = [];
      for (let i = 0; i < 8; i++) {
        const n = i + 1;
        rows.push({ k: `project_${i}_title_ar`, l: `المشروع ${n} — العنوان (عربي)` });
        rows.push({ k: `project_${i}_title_en`, l: `Project ${n} — Title (English)`, dir: "ltr" });
        rows.push({ k: `project_${i}_title_ku`, l: `پڕۆژە ${n} — سەردێڕ (کوردی)` });
        rows.push({ k: `project_${i}_overview_ar`, l: `المشروع ${n} — نظرة عامة (عربي)`, type: "textarea" });
        rows.push({ k: `project_${i}_overview_en`, l: `Project ${n} — Overview (English)`, type: "textarea", dir: "ltr" });
        rows.push({ k: `project_${i}_overview_ku`, l: `پڕۆژە ${n} — پوختە (کوردی)`, type: "textarea" });
        rows.push({ k: `project_${i}_image`, l: `المشروع ${n} — صورة الغلاف (اختياري)`, dir: "ltr", uploadKind: "image" });
        rows.push({ k: `project_${i}_screenshots`, l: `المشروع ${n} — لقطات شاشة (متعدد)`, type: "textarea", dir: "ltr", uploadKind: "image", appendUpload: true });
      }
      return rows;
    })(),
  },
  {
    id: "whyus",
    label: "⭐ لماذا مدينتي (8 نقاط)",
    hint: "الأسباب الثمانية التي تميّز شركتكم — نص قصير لكل نقطة بثلاث لغات.",
    fields: (() => {
      const rows = [];
      for (let i = 0; i < 8; i++) {
        const n = i + 1;
        rows.push({ k: `why_${i}_ar`, l: `النقطة ${n} — (عربي)` });
        rows.push({ k: `why_${i}_en`, l: `Reason ${n} — (English)`, dir: "ltr" });
        rows.push({ k: `why_${i}_ku`, l: `هۆکار ${n} — (کوردی)` });
      }
      return rows;
    })(),
  },
  {
    id: "sectors",
    label: "🏢 القطاعات (10 قطاعات)",
    hint: "القطاعات التي تخدمها الشركة — اسم كل قطاع بثلاث لغات.",
    fields: (() => {
      const rows = [];
      for (let i = 0; i < 10; i++) {
        const n = i + 1;
        rows.push({ k: `sector_${i}_ar`, l: `القطاع ${n} — (عربي)` });
        rows.push({ k: `sector_${i}_en`, l: `Sector ${n} — (English)`, dir: "ltr" });
        rows.push({ k: `sector_${i}_ku`, l: `کەرتی ${n} — (کوردی)` });
      }
      return rows;
    })(),
  },
  {
    id: "stages",
    label: "🧭 مراحل العمل (7 مراحل)",
    hint: "منهجية العمل من التحليل حتى الصيانة — عنوان + وصف لكل مرحلة.",
    fields: (() => {
      const rows = [];
      for (let i = 0; i < 7; i++) {
        const n = i + 1;
        rows.push({ k: `stage_${i}_title_ar`, l: `المرحلة ${n} — العنوان (عربي)` });
        rows.push({ k: `stage_${i}_title_en`, l: `Stage ${n} — Title (English)`, dir: "ltr" });
        rows.push({ k: `stage_${i}_title_ku`, l: `قۆناغی ${n} — سەردێڕ (کوردی)` });
        rows.push({ k: `stage_${i}_desc_ar`, l: `المرحلة ${n} — الوصف (عربي)`, type: "textarea" });
        rows.push({ k: `stage_${i}_desc_en`, l: `Stage ${n} — Description (English)`, type: "textarea", dir: "ltr" });
        rows.push({ k: `stage_${i}_desc_ku`, l: `قۆناغی ${n} — پێناسە (کوردی)`, type: "textarea" });
      }
      return rows;
    })(),
  },
  {
    id: "quotecalc",
    label: "💰 حاسبة العرض (تسعير)",
    hint: "أسعار قاعدية (Base) + سعر لكل مستخدم + سعر لكل وحدة، لكل نوع نظام. اترك فارغًا لاستخدام الافتراضي.",
    fields: (() => {
      const types = [
        ["hotel", "الفنادق"],
        ["erp", "ERP"],
        ["scada", "SCADA"],
        ["website", "موقع"],
        ["mobile", "موبايل"],
        ["ai", "AI"],
        ["security", "أمنية"],
      ];
      const rows = [];
      types.forEach(([k, lbl]) => {
        rows.push({ k: `quote_${k}_base`, l: `${lbl} — السعر الأساسي ($)`, dir: "ltr" });
        rows.push({ k: `quote_${k}_per_user`, l: `${lbl} — سعر لكل مستخدم ($)`, dir: "ltr" });
        rows.push({ k: `quote_${k}_per_module`, l: `${lbl} — سعر لكل وحدة ($)`, dir: "ltr" });
      });
      return rows;
    })(),
  },
  {
    id: "brand",
    label: "🎨 الهوية البصرية",
    hint: "الشعار الرسمي وألوان العلامة التجارية.",
    fields: [
      { k: "brand_logo_url", l: "الشعار (Logo URL أو رفع)", dir: "ltr", uploadKind: "image" },
      { k: "brand_favicon_url", l: "أيقونة المتصفح (Favicon)", dir: "ltr", uploadKind: "image" },
      { k: "brand_primary_color", l: "اللون الأساسي (Primary — hex مثل #00f260)", ph: "#00f260", dir: "ltr" },
      { k: "brand_bg_color", l: "خلفية الموقع (Background — hex)", ph: "#050914", dir: "ltr" },
    ],
  },
  {
    id: "chatbot",
    label: "💬 توجيهات الشات‌بوت (System Prompts)",
    hint: "التعليمات التي يتّبعها الشات‌بوت في كل لغة. اتركها فارغة لاستخدام الافتراضي.",
    fields: [
      { k: "chatbot_prompt_ar", l: "توجيهات الشات‌بوت (عربي)", type: "textarea" },
      { k: "chatbot_prompt_en", l: "Chatbot system prompt (English)", type: "textarea", dir: "ltr" },
      { k: "chatbot_prompt_ku", l: "ڕێنمایی چات‌بۆت (کوردی)", type: "textarea" },
    ],
  },
  {
    id: "legal",
    label: "⚖️ الصفحات القانونية",
    hint: "نصوص كاملة لصفحتَي الخصوصية والشروط. اترك أي حقل فارغًا لاستخدام النص الافتراضي.",
    fields: [
      { k: "legal_privacy_ar", l: "سياسة الخصوصية (عربي)", type: "textarea" },
      { k: "legal_privacy_en", l: "Privacy Policy (English)", type: "textarea", dir: "ltr" },
      { k: "legal_privacy_ku", l: "سیاسەتی تایبەتی (کوردی)", type: "textarea" },
      { k: "legal_terms_ar", l: "شروط الاستخدام (عربي)", type: "textarea" },
      { k: "legal_terms_en", l: "Terms of Service (English)", type: "textarea", dir: "ltr" },
      { k: "legal_terms_ku", l: "مەرجەکانی بەکارهێنان (کوردی)", type: "textarea" },
    ],
  },
];

function SiteContentManager({ adminKey }) {
  const [values, setValues] = useState({});
  const [savingId, setSavingId] = useState("");
  const [openSection, setOpenSection] = useState("identity");
  const [uploadingKey, setUploadingKey] = useState("");

  useEffect(() => {
    fetchSettings().then((r) => setValues(r.data || {})).catch(() => {});
  }, []);

  const setAt = (k, v) => setValues((s) => ({ ...s, [k]: v }));

  const saveSection = async (section) => {
    setSavingId(section.id);
    try {
      const payload = {};
      section.fields.forEach((f) => { payload[f.k] = values[f.k] || ""; });
      await updateSettings(adminKey, payload);
      toast.success(`تم حفظ قسم "${section.label}"`);
    } catch { toast.error("فشل الحفظ"); }
    finally { setSavingId(""); }
  };

  const uploadFor = async (key, kind, file) => {
    if (!file) return;
    setUploadingKey(key);
    try {
      const r = await uploadImage(adminKey, file);
      setAt(key, r.data.url);
      await updateSettings(adminKey, { [key]: r.data.url });
      toast.success(`تم رفع الملف`);
    } catch { toast.error(`فشل الرفع (تأكد من نوع الملف)`); }
    finally { setUploadingKey(""); }
  };

  const parseUrlList = (val) => (val || "").split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
  const joinUrlList = (arr) => arr.join("\n");

  const uploadManyAppend = async (key, files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length) return;
    setUploadingKey(key);
    try {
      const uploaded = [];
      for (const file of list) {
        try {
          const r = await uploadImage(adminKey, file);
          if (r?.data?.url) uploaded.push(r.data.url);
        } catch { /* skip failed file */ }
      }
      if (!uploaded.length) { toast.error("فشل رفع الملفات"); return; }
      const existing = parseUrlList(values[key]);
      const merged = joinUrlList([...existing, ...uploaded]);
      setAt(key, merged);
      await updateSettings(adminKey, { [key]: merged });
      toast.success(`تم رفع ${uploaded.length} صورة`);
    } finally { setUploadingKey(""); }
  };

  const removeUrlAt = async (key, url) => {
    const remaining = parseUrlList(values[key]).filter((u) => u !== url);
    const joined = joinUrlList(remaining);
    setAt(key, joined);
    try { await updateSettings(adminKey, { [key]: joined }); toast.success("تم الحذف"); }
    catch { toast.error("فشل الحذف"); }
  };

  const clearField = async (key) => {
    setAt(key, "");
    try { await updateSettings(adminKey, { [key]: "" }); toast.success("تم الحذف"); }
    catch { toast.error("فشل الحذف"); }
  };

  const dragState = useRef({ key: "", from: -1 });
  const onDragStart = (key, i) => { dragState.current = { key, from: i }; };
  const onDragOver = (e) => { e.preventDefault(); };
  const onDropAt = async (key, to) => {
    const { key: fromKey, from } = dragState.current;
    dragState.current = { key: "", from: -1 };
    if (fromKey !== key || from < 0 || from === to) return;
    const list = parseUrlList(values[key]);
    if (from >= list.length || to >= list.length) return;
    const [moved] = list.splice(from, 1);
    list.splice(to, 0, moved);
    const joined = joinUrlList(list);
    setAt(key, joined);
    try { await updateSettings(adminKey, { [key]: joined }); toast.success("تم إعادة الترتيب"); }
    catch { toast.error("فشل الحفظ"); }
  };

  const inputCls = "w-full rounded-xl bg-[#0d1836] border border-white/10 px-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[color:var(--primary)]";
  const textareaCls = inputCls + " resize-y min-h-[80px]";

  return (
    <div className="space-y-4" data-testid="content-manager">
      <div>
        <h2 className="text-xl font-extrabold">الهوية والمحتوى</h2>
        <p className="text-sm text-slate-400 mt-1">عدّل النصوص والصور والفيديوهات الرئيسية للموقع. اترك أي حقل فارغًا لاستخدام القيمة الافتراضية.</p>
      </div>

      {CONTENT_SECTIONS.map((section) => {
        const isOpen = openSection === section.id;
        return (
          <div key={section.id} className="card-surface rounded-2xl overflow-hidden" data-testid={`content-section-${section.id}`}>
            <button
              onClick={() => setOpenSection(isOpen ? "" : section.id)}
              className="w-full flex items-center justify-between p-5 text-start hover:bg-white/5 transition-colors"
              data-testid={`content-toggle-${section.id}`}
            >
              <div>
                <div className="font-extrabold text-lg">{section.label}</div>
                <div className="text-xs text-slate-500 mt-0.5">{section.hint}</div>
              </div>
              <span className="text-primary text-2xl">{isOpen ? "−" : "+"}</span>
            </button>
            {isOpen && (
              <div className="p-5 pt-0 space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  {section.fields.map((f) => (
                    <div key={f.k} className={f.type === "textarea" ? "sm:col-span-2" : ""}>
                      <label className="block text-xs font-semibold text-slate-300 mb-1.5">{f.l}</label>
                      {f.type === "textarea" ? (
                        <div className="space-y-2">
                          <textarea
                            value={values[f.k] || ""}
                            onChange={(e) => setAt(f.k, e.target.value)}
                            placeholder={f.ph || (f.appendUpload ? "رابط لكل سطر — أو ارفع صور مباشرة" : "")}
                            dir={f.dir || undefined}
                            className={textareaCls}
                            rows={3}
                            data-testid={`content-input-${f.k}`}
                          />
                          {f.appendUpload && (
                            <div className="flex items-center gap-2 flex-wrap">
                              <label className="inline-flex items-center gap-1.5 rounded-xl border border-white/15 hover:border-[color:var(--primary)] px-3 py-2 text-xs font-bold cursor-pointer" data-testid={`content-upload-multi-${f.k}`}>
                                {uploadingKey === f.k ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                                رفع صور متعددة
                                <input
                                  type="file"
                                  accept="image/*"
                                  multiple
                                  className="hidden"
                                  onChange={(e) => uploadManyAppend(f.k, e.target.files)}
                                />
                              </label>
                              <span className="text-[11px] text-slate-500">اختر عدة ملفات دفعة واحدة — اسحب الصور لإعادة الترتيب</span>
                            </div>
                          )}
                          {f.appendUpload && parseUrlList(values[f.k]).length > 0 && (
                            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 pt-1" data-testid={`content-thumbs-${f.k}`}>
                              {parseUrlList(values[f.k]).map((u, i) => (
                                <div
                                  key={`${u}-${i}`}
                                  className="relative group aspect-video rounded-lg overflow-hidden border border-white/10 bg-[#0d1836] cursor-move"
                                  draggable
                                  onDragStart={() => onDragStart(f.k, i)}
                                  onDragOver={onDragOver}
                                  onDrop={() => onDropAt(f.k, i)}
                                  data-testid={`content-thumb-${f.k}-${i}`}
                                  title="اسحب لإعادة الترتيب"
                                >
                                  <img src={toAbsolute(u)} alt="" className="w-full h-full object-cover pointer-events-none" onError={(e) => { e.currentTarget.style.opacity = 0.3; }} />
                                  {i === 0 && (
                                    <span className="absolute bottom-1 start-1 text-[10px] font-bold rounded-full px-1.5 py-0.5 bg-[color:var(--primary)]/95 text-[#04120a] pointer-events-none">
                                      الغلاف
                                    </span>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => removeUrlAt(f.k, u)}
                                    className="absolute top-1 end-1 rounded-full bg-black/70 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                    title="حذف"
                                    data-testid={`content-thumb-remove-${f.k}-${i}`}
                                  >
                                    <X size={12} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={values[f.k] || ""}
                              onChange={(e) => setAt(f.k, e.target.value)}
                              placeholder={f.ph || ""}
                              dir={f.dir || undefined}
                              className={inputCls}
                              data-testid={`content-input-${f.k}`}
                            />
                            {f.uploadKind && (
                              <label className="shrink-0 inline-flex items-center gap-1.5 rounded-xl border border-white/15 hover:border-[color:var(--primary)] px-3 text-xs font-bold cursor-pointer" data-testid={`content-upload-${f.k}`}>
                                {uploadingKey === f.k ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                                <input
                                  type="file"
                                  accept={f.uploadKind === "video" ? "video/mp4,video/webm" : "image/*"}
                                  className="hidden"
                                  onChange={(e) => uploadFor(f.k, f.uploadKind, e.target.files?.[0])}
                                />
                              </label>
                            )}
                          </div>
                          {f.uploadKind === "image" && values[f.k] && (
                            <div className="relative inline-block group" data-testid={`content-thumb-${f.k}`}>
                              <img src={toAbsolute(values[f.k])} alt="" className="h-20 w-32 object-cover rounded-lg border border-white/10 bg-[#0d1836]" onError={(e) => { e.currentTarget.style.opacity = 0.3; }} />
                              <button
                                type="button"
                                onClick={() => clearField(f.k)}
                                className="absolute top-1 end-1 rounded-full bg-black/70 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                title="حذف"
                                data-testid={`content-thumb-clear-${f.k}`}
                              >
                                <X size={12} />
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => saveSection(section)}
                  disabled={savingId === section.id}
                  className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-bold text-[#04120a] disabled:opacity-60"
                  style={{ background: "var(--primary)" }}
                  data-testid={`content-save-${section.id}`}
                >
                  {savingId === section.id ? <><Loader2 size={16} className="animate-spin" /> جارٍ الحفظ...</> : "حفظ التغييرات"}
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}


const PROJECT_LABELS = [
  "أنظمة إدارة الفنادق",
  "أنظمة ERP",
  "أنظمة SCADA",
  "مواقع إلكترونية",
  "تطبيقات الهاتف",
  "حلول الذكاء الاصطناعي",
  "الأنظمة الأمنية",
];

function ProjectVideosCard({ adminKey }) {  const [videos, setVideos] = useState(Array(8).fill(""));
  const [saving, setSaving] = useState(false);
  const [uploadingIdx, setUploadingIdx] = useState(-1);

  useEffect(() => {
    fetchSettings().then((r) => {
      const d = r.data || {};
      setVideos(Array.from({ length: 8 }, (_, i) => d[`project_video_${i}`] || ""));
    }).catch(() => {});
  }, []);

  const setAt = (i, v) => setVideos((arr) => arr.map((x, k) => (k === i ? v : x)));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      videos.forEach((v, i) => { payload[`project_video_${i}`] = v; });
      await updateSettings(adminKey, payload);
      toast.success("تم حفظ فيديوهات المشاريع");
    } catch { toast.error("فشل الحفظ"); }
    finally { setSaving(false); }
  };

  const uploadFor = async (i, file) => {
    if (!file) return;
    setUploadingIdx(i);
    try {
      const r = await uploadImage(adminKey, file);
      const url = r.data.url;
      setAt(i, url);
      await updateSettings(adminKey, { [`project_video_${i}`]: url });
      toast.success("تم رفع الفيديو");
    } catch { toast.error("فشل الرفع (تأكد أن الملف MP4/WebM وأقل من 100MB)"); }
    finally { setUploadingIdx(-1); }
  };

  const clearAt = async (i) => {
    setAt(i, "");
    await updateSettings(adminKey, { [`project_video_${i}`]: "" });
    toast.success("تم إعادة الفيديو للافتراضي");
  };

  const inputCls = "w-full rounded-xl bg-[#0d1836] border border-white/10 px-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[color:var(--primary)]";

  return (
    <div className="card-surface rounded-2xl p-6 max-w-3xl" data-testid="settings-project-videos-card">
      <h3 className="font-bold mb-1">فيديوهات المشاريع (Intro Videos)</h3>
      <p className="text-sm text-slate-400 mb-2">يظهر لكل مشروع فيديو تعريفي عند فتح صفحته من قسم &quot;مشاريعنا&quot;.</p>
      <p className="text-xs text-slate-500 mb-5">
        الصق رابط <code className="text-primary">MP4/WebM</code> مباشرًا، أو ارفع ملف فيديو (حتى 100MB). اترك الحقل فارغًا لاستخدام الفيديو الافتراضي.
      </p>
      <div className="space-y-4">
        {PROJECT_LABELS.map((label, i) => (
          <div key={i} className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <div className="text-sm font-semibold min-w-[10rem] shrink-0">
              <span className="text-primary">#{i + 1}</span> {label}
            </div>
            <input
              type="text"
              value={videos[i]}
              onChange={(e) => setAt(i, e.target.value)}
              placeholder="https://... .mp4"
              dir="ltr"
              className={inputCls}
              data-testid={`settings-project-video-${i}`}
            />
            <div className="flex gap-2 shrink-0">
              <label className="inline-flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-2 text-xs font-bold cursor-pointer hover:border-[color:var(--primary)]" data-testid={`settings-project-video-upload-${i}`}>
                {uploadingIdx === i ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                رفع
                <input type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden" onChange={(e) => uploadFor(i, e.target.files?.[0])} />
              </label>
              {videos[i] && (
                <button onClick={() => clearAt(i)} className="rounded-lg border border-white/15 hover:border-red-400 text-red-400 px-3 py-2 text-xs font-bold" data-testid={`settings-project-video-clear-${i}`}>
                  إعادة
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
      <button
        onClick={save}
        disabled={saving}
        className="mt-6 inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-bold text-[#04120a] disabled:opacity-60"
        style={{ background: "var(--primary)" }}
        data-testid="settings-project-videos-save"
      >
        {saving ? <><Loader2 size={16} className="animate-spin" /> جارٍ الحفظ...</> : "حفظ روابط الفيديوهات"}
      </button>
    </div>
  );
}

export default function Admin() {
  const [key, setKey] = useState(localStorage.getItem(STORE_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [input, setInput] = useState("");
  const [error, setError] = useState("");
  const [tab, setTab] = useState("leads");

  const verify = useCallback(async (k) => {
    try { await adminLogin(k); setAuthed(true); } catch { setAuthed(false); localStorage.removeItem(STORE_KEY); }
  }, []);

  useEffect(() => { if (key) verify(key); }, [key, verify]);

  const onLogin = async (e) => {
    e.preventDefault();
    setError("");
    try { await adminLogin(input); localStorage.setItem(STORE_KEY, input); setKey(input); setAuthed(true); }
    catch { setError("مفتاح غير صحيح"); }
  };

  const logout = () => { localStorage.removeItem(STORE_KEY); setKey(""); setAuthed(false); setInput(""); };

  if (!authed) {
    return (
      <div className="min-h-screen grid place-items-center px-5" dir="rtl">
        <form onSubmit={onLogin} className="card-surface rounded-3xl p-8 w-full max-w-sm text-center" data-testid="admin-login">
          <BrandMark size={64} className="mx-auto mb-4" />
          <div className="h-12 w-12 rounded-2xl grid place-items-center mx-auto mb-4" style={{ background: "rgba(0,242,96,0.12)" }}><Lock size={22} className="text-primary" /></div>
          <h1 className="text-xl font-extrabold mb-6">لوحة تحكم مدينتي</h1>
          <input type="password" value={input} onChange={(e) => setInput(e.target.value)} placeholder="مفتاح الدخول" className="w-full rounded-xl bg-[#0d1836] border border-white/10 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[color:var(--primary)] mb-3 text-center" data-testid="admin-key-input" />
          {error && <p className="text-red-400 text-sm mb-3" data-testid="admin-error">{error}</p>}
          <button type="submit" className="w-full rounded-xl px-6 py-3 text-sm font-bold text-[#04120a]" style={{ background: "var(--primary)" }} data-testid="admin-login-btn">دخول</button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-5 sm:p-8" dir="rtl" data-testid="admin-dashboard">
      <div className="mx-auto max-w-5xl">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3"><BrandMark size={44} /><h1 className="text-2xl font-extrabold">لوحة التحكم</h1></div>
          <button onClick={logout} className="flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm hover:border-red-400 text-slate-300" data-testid="admin-logout"><LogOut size={15} /> خروج</button>
        </div>

        <div className="flex flex-wrap gap-2 mb-8 border-b border-white/10 pb-3">
          {TABS.map((tb) => (
            <button key={tb.id} onClick={() => setTab(tb.id)} className={`rounded-full px-5 py-2 text-sm font-bold transition-colors ${tab === tb.id ? "text-[#04120a]" : "text-slate-300 border border-white/10 hover:border-white/30"}`} style={tab === tb.id ? { background: "var(--primary)" } : {}} data-testid={`tab-${tb.id}`}>
              {tb.label}
            </button>
          ))}
        </div>

        {tab === "leads" && <LeadsManager adminKey={key} />}
        {tab === "blog" && <ContentManager resType="blog" adminKey={key} />}
        {tab === "case-studies" && <ContentManager resType="case-studies" adminKey={key} />}
        {tab === "team" && <ContentManager resType="team" adminKey={key} />}
        {tab === "testimonials" && <ContentManager resType="testimonials" adminKey={key} />}
        {tab === "faqs" && <ContentManager resType="faqs" adminKey={key} />}
        {tab === "achievements" && <ContentManager resType="achievements" adminKey={key} />}
        {tab === "jobs" && <ContentManager resType="jobs" adminKey={key} />}
        {tab === "milestones" && <ContentManager resType="milestones" adminKey={key} />}
        {tab === "partners" && <ContentManager resType="partners" adminKey={key} />}
        {tab === "certifications" && <ContentManager resType="certifications" adminKey={key} />}
        {tab === "video-testimonials" && <ContentManager resType="video-testimonials" adminKey={key} />}
        {tab === "applications" && <ContentManager resType="applications" adminKey={key} />}
        {tab === "resources" && <ContentManager resType="resources" adminKey={key} />}
        {tab === "gallery" && <ContentManager resType="gallery" adminKey={key} />}
        {tab === "bookings" && <BookingsManager adminKey={key} />}
        {tab === "tickets" && <TicketsManager adminKey={key} />}
        {tab === "content" && <SiteContentManager adminKey={key} />}
        {tab === "newsletter" && <NewsletterManager adminKey={key} />}
        {tab === "settings" && <SettingsManager adminKey={key} />}
      </div>
    </div>
  );
}
